import { fabric } from 'fabric';
import { Injector } from '@angular/core';
import { ItemModel } from '../../../../models/itemModel';
import { FrameItem } from '../../../../models/session';
import { ResourceItemModel } from '../../../../models/resource';
import {
  PDF_PAGE_DEFAULT_HEIGHT,
  PDF_PAGE_DEFAULT_WIDTH,
} from '../../iframe/additional-apps.utils';
import {
  PDF_PAGE_LEFT_PADDING,
  RENDERED_PDF_PAGES_SCALE,
} from '../../pdf-canvas-item/pdf-services/pdf-page-to-fabric.service';
import { HiddenPageComponent } from '../../../../standalones/components/pdf-viewer/hidden-page/hidden-page.component';
import { isBoardImage } from '../../../common/y-session';
import { PdfManagerService } from '../../pdf-canvas-item/pdf-services/pdf-manager.service';
import type { BoundingRect } from '../../../../services/sessions-vpt.service';
import { BoardItemComponentClass, FabricControls } from './board.item';
import { CreationContext } from './board-item-configurations';
import { PDFImagesConfigurations } from './board-images-pdf-configurations';
import { BoardItemsFactory } from './board-items-factory';
import { BoardImagePdf } from './board-image-pdf';

export type FabricPdfPage = fabric.Image & {
  skipRemoteAddition?: boolean;
  skipRemoteDeletion?: boolean;
  pageIndex?: number;
  isRendering?: boolean;
  shiftFromTop?: number;
};

export class FabricGroup extends fabric.Group {
  constructor(config) {
    super(
      [],
      // perPixelTargetFind is needed because adding the image in a group even when its (crossOrigin = 'anonymous') throws a tainted canvas error
      { perPixelTargetFind: false },
    );
    this.top = config.top;
    this.left = config.left;
    this.objectCaching = false;
  }
  static _fromObject(
    className: string,
    object: Object,
    callback?: Function,
    extraParam?: any,
  ): fabric.Group {
    return new fabric.Group();
  }
}

const PDFInvisibleControls = Object.values(FabricControls);
export class BoardImagesPdf extends BoardItemComponentClass<
  PDFImagesConfigurations,
  HiddenPageComponent
>(FabricGroup) {
  private boardItemsFactory: BoardItemsFactory;
  private pdfManagerService: PdfManagerService;

  AngularComponentClass = HiddenPageComponent;

  constructor(
    injector: Injector,
    configurations: PDFImagesConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.PDFImages, configurations, PDFInvisibleControls, creationContext);

    this.boardItemsFactory = injector.get<BoardItemsFactory>(BoardItemsFactory);
    this.pdfManagerService = injector.get<PdfManagerService>(PdfManagerService);
    this.padding = PDF_PAGE_LEFT_PADDING;

    this.focusOnInsertion =
      creationContext.isNewlyCreatedItem && creationContext.renderInNearestFreeSpace;
  }

  protected onFabricAdded() {
    super.onFabricAdded();

    this.addBoardImageForEachPage();
    this.addPlaceholderForOverlay();
  }

  protected onFabricRemoved() {
    super.onFabricRemoved();

    (this as any as fabric.Group).getObjects().forEach((object) => {
      object.fire('removed');
    });
  }

  private addPlaceholderForOverlay() {
    if (!this.isOnStaticCanvas()) {
      return;
    }
    (this as any as fabric.Group).addWithUpdate(
      // We are using this rect as a placeholder,
      // since the group by itself can not have a size other than the size of the items inside it
      // The size here is the same as the overlay, so it is used as a placeholder for the PDF overlay in static canvases
      new fabric.Rect({
        width: PDF_PAGE_DEFAULT_WIDTH,
        height: PDF_PAGE_DEFAULT_HEIGHT,
        opacity: 0.001,
        top: this.configurations.top,
        left: this.configurations.left,
      }),
    );
  }

  private async addBoardImageForEachPage() {
    if (this.isOnStaticCanvas()) {
      return;
    }
    if (!this.canvas) {
      return;
    }

    const boardImages: BoardImagePdf[] = [];
    const { imagesSizes, numberOfPages, imagesToken, imagesPrefix, selectedIndices } =
      this.configurations;

    if (
      !imagesSizes ||
      numberOfPages === undefined ||
      !imagesToken ||
      !imagesPrefix ||
      !selectedIndices
    ) {
      return;
    }

    const decompressedImagesSizes = this.pdfManagerService.decompressImagesSizes({
      imagesSizes,
      numberOfPages,
      imagesToken,
      imagesPrefix,
    });

    let totalHeight = 0;
    for (const i of selectedIndices) {
      // in case the page has a previous shift but now a single page, we remove that shift
      const imageUrl = this.pdfManagerService.getImageUrl(
        i,
        this.configurations.imagesPrefix,
        this.configurations.imagesToken,
      );

      const boardImage = this.boardItemsFactory.createBoardItem<ItemModel.PDFImage>(
        ItemModel.PDFImage,
        {
          url: imageUrl,
          top: this.configurations.top + totalHeight,
          left: this.configurations.left,
          scaleX: RENDERED_PDF_PAGES_SCALE,
          scaleY: RENDERED_PDF_PAGES_SCALE,
          width: decompressedImagesSizes[i].width,
          height: decompressedImagesSizes[i].height,
        },
      );

      boardImage.shiftFromTop = totalHeight;

      boardImages.push(boardImage);

      totalHeight += decompressedImagesSizes[i].height;
    }

    this.addWithUpdateBatch(boardImages, this.canvas);

    this.canvas?.requestRenderAll();
  }

  /**
   * The bounds of the PDF based on its pages, following the same layout as `addBoardImageForEachPage`.
   * Needed because the group has no size until its pages are added to it, which only happens when it's added to the canvas.
   */
  override getBoardItemBoundingRect(): BoundingRect {
    const { top = 0, left = 0 } = this.configurations;
    const { imagesSizes, numberOfPages, imagesToken, imagesPrefix, selectedIndices } =
      this.configurations;

    if (
      !imagesSizes ||
      numberOfPages === undefined ||
      !imagesToken ||
      !imagesPrefix ||
      !selectedIndices?.length
    ) {
      return { top, left, width: PDF_PAGE_DEFAULT_WIDTH, height: PDF_PAGE_DEFAULT_HEIGHT };
    }

    const decompressedImagesSizes = this.pdfManagerService.decompressImagesSizes({
      imagesSizes,
      numberOfPages,
      imagesToken,
      imagesPrefix,
    });

    let totalHeight = 0;
    let width = 0;
    let height = 0;
    for (const i of selectedIndices) {
      const pageSize = decompressedImagesSizes[i];
      if (!pageSize) {
        continue;
      }
      width = Math.max(width, pageSize.width * RENDERED_PDF_PAGES_SCALE);
      height = Math.max(height, totalHeight + pageSize.height * RENDERED_PDF_PAGES_SCALE);
      totalHeight += pageSize.height;
    }

    return {
      top,
      left,
      width: width || PDF_PAGE_DEFAULT_WIDTH,
      height: height || PDF_PAGE_DEFAULT_HEIGHT,
    };
  }

  // This is the same native method from fabric.Group `addWithUpdate` but optimized for adding batches of objects
  // for original version that handles one object, refer to: https://github.com/fabricjs/fabric.js/blob/7db80e5bb674b52b0c2f39aa5a124e6c4a77627a/dist/fabric.js#L20174C38-L20175C7
  // We also added manual firing for `added` event as it is needed by the boardImages to call onFabricAdded
  addWithUpdateBatch(objects: fabric.Object[], canvas) {
    // Do one-time state restoration
    (this as any as fabric.Group)._restoreObjectsState();

    fabric.util.resetObjectTransform(this);

    // Add all objects at once
    for (const object of objects) {
      (this as any as fabric.Group)._objects.push(object);
      object.group = this as any as fabric.Group;
      object._set('canvas', canvas);
    }

    // Do one-time coordinate updates
    (this as any as fabric.Group)._calcBounds();
    (this as any as fabric.Group)._updateObjectsCoords();
    this.dirty = true;

    this.setCoords();

    for (const object of objects) {
      // Because the board item listen to that event, and it doesn't get automatically triggered when adding in a group instead of in a canvas
      object.fire('added');
    }

    return this;
  }

  protected setStateFromConfigurations(configurations: PDFImagesConfigurations) {
    // Overriding width and height update because it should only depend on the size of the content
    configurations.height = this.height ?? PDF_PAGE_DEFAULT_HEIGHT;
    configurations.width = this.width ?? PDF_PAGE_DEFAULT_WIDTH;
    super.setStateFromConfigurations(configurations);

    // Notifying the pages that the group has been changed due to changes from the server
    // This is needed to sync the HTML or create it now that the page might enter the viewport
    const objects = (this as any as fabric.Group)._objects;
    for (const object of objects) {
      if (isBoardImage(object)) {
        object.syncHtmlWithFabric();
      }
    }
  }

  override openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = true;
    this.lockScalingY = true;
    this.lockUniScaling = false;
    this.lockSkewingX = false;
    this.lockSkewingY = false;
    this.lockScalingFlip = false;

    this.evented = true;
    this.propagateCanvasMouseDownToObject = false; // When the object is unlocked we don't need this as the behavior will be normal,
    // and we don't want an extra performance overhead.
  }

  closeLock() {
    super.closeLock();

    // when the pdf is locked we need to treat it as if it doesn't exist, to enable multi-select of scribbles on top of it
    // We instead, can implement the needed actions on mouse down in the canvasMouseDown method.
    this.evented = false;
    this.propagateCanvasMouseDownToObject = true;
  }

  protected onCanvasMouseDown(mouseDownAffectedOtherObjects: boolean) {
    super.onCanvasMouseDown(mouseDownAffectedOtherObjects);
    if (mouseDownAffectedOtherObjects) {
      // the click was on another object above the PDF and the object didn't receive another click
      return;
    }

    this.showLockSnackBarIfObjectLocked();

    this.canvas?.setActiveObject(this);
    this.canvas?.requestRenderAll(); // to render the blue border
  }

  override toFrameItem(): FrameItem {
    const encodedBoardItem = super.toFrameItem();

    // No need to sync the height as we don't want the loading indicator and the preview to take the height of all pages
    encodedBoardItem.position?.set('height', `${PDF_PAGE_DEFAULT_HEIGHT}`);

    return {
      ...encodedBoardItem,
      model: ItemModel.Resource,
      resource_state: {
        imagesSizes: this.configurations.imagesSizes,
        resource_item_model: ResourceItemModel.PDF_IMAGES,
        imagesPrefix: this.configurations.imagesPrefix,
        imagesToken: this.configurations.imagesToken,
        // Durable URL of the original uploaded PDF/office file this board was
        // converted from, so the board is traceable back to its source.
        resource_url: this.configurations.sourceUrl,
        numberOfPages: this.configurations.numberOfPages,
        selectedIndices:
          this.configurations.selectedIndices?.length === this.configurations.numberOfPages
            ? []
            : this.configurations.selectedIndices,
        // to improve memory consumption we consider an empty array to mean all pages are selected
      },
      content_id: this.configurations.contentId ?? '',
      migratedFromHtmlToFabric: this.configurations.migratedFromHtmlToFabric,
    };
  }

  override hasHtmlContent() {
    return false;
  }

  override createHtmlComponent() {
    if (this.isOnStaticCanvas()) {
      // For displaying the layout in the boards manager
      super.createHtmlComponent();
    }
  }
}
